import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { m as motion, useScroll, useTransform } from "framer-motion";
import api from "../lib/api";
import SafeImage from "../components/SafeImage";
import KineticLines from "../components/KineticLines";
import BookCard from "../components/BookCard";
import Marquee from "../components/Marquee";
import NumberedChapter from "../components/NumberedChapter";
import Seo from "../components/Seo";

export default function Home() {
  const [novels, setNovels] = useState([]);
  const [featured, setFeatured] = useState(null);
  const [news, setNews] = useState([]);
  const [settings, setSettings] = useState(null);
  const { scrollY } = useScroll();
  const heroY = useTransform(scrollY, [0, 800], [0, 200]);
  const heroOpacity = useTransform(scrollY, [0, 500], [1, 0.15]);

  useEffect(() => {
    Promise.all([
      api.get("/novels"),
      api.get("/news", { params: { limit: 3 } }),
      api.get("/settings"),
    ]).then(([n, nw, s]) => {
      setNovels(n.data);
      setFeatured(n.data.find((x) => x.featured) || n.data[0]);
      setNews(nw.data);
      setSettings(s.data);
    }).catch(() => {});
  }, []);

  return (
    <div>
      <Seo
        path="/"
        title="Auteur de romans historiques, d'anticipation et d'aventure"
        description="Site officiel de Bruno Courtin, auteur français. Découvrez ses romans (Malo, Terra Australis, Les Chaînes du Vivant, Mauritia, L'Arbre qui cachait la forêt) et son univers littéraire."
        image="/api/files/malo-cover.png"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "WebSite",
          name: "Bruno Courtin",
          alternateName: "Site officiel de l'auteur Bruno Courtin",
          url: "https://brunocourtin.fr",
          inLanguage: "fr-FR",
          author: {
            "@type": "Person",
            name: "Bruno Courtin",
            jobTitle: "Auteur",
            description: "Auteur français de romans historiques, d'aventure et d'anticipation.",
            url: "https://brunocourtin.fr/auteur",
          },
        }}
      />
      {/* ============ HERO ============ */}
      <section className="relative theme-ivory min-h-[100vh] pt-32 pb-24 overflow-hidden" data-testid="hero-section">
        <motion.div
          style={{ y: heroY, opacity: heroOpacity }}
          className="absolute inset-0 z-0"
        >
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{
              backgroundImage: `url(${process.env.REACT_APP_BACKEND_URL}/api/files/malo-carte-1.png)`,
              filter: "sepia(0.5) contrast(0.92) brightness(0.98)",
              opacity: 0.22,
            }}
          />
          <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(245,239,228,0.55) 0%, rgba(245,239,228,0.85) 60%, var(--ivory) 100%)" }} />
        </motion.div>

        <div className="relative z-10 max-w-[1440px] mx-auto px-6 lg:px-12 grid grid-cols-12 gap-6 pt-16 md:pt-28">
          <div className="col-span-12 md:col-span-8">
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.8 }}
              className="eyebrow mb-8"
              data-testid="hero-eyebrow"
            >
              Site officiel — Auteur
            </motion.p>

            <h1 className="font-display font-light text-[14vw] md:text-[9.5vw] lg:text-[8.5vw] leading-[0.9] tracking-[-0.02em] text-[var(--ink)]" data-testid="hero-title">
              <div><KineticLines lines={["Bruno"]} delay={0.15} /></div>
              <div><KineticLines lines={["Courtin"]} delay={0.35} className="italic" /></div>
            </h1>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.2, duration: 0.9 }}
              className="mt-10 md:mt-14 max-w-2xl"
            >
              <p className="eyebrow mb-4" style={{ color: "var(--copper)" }}>
                Romans historiques · Anticipation · Aventure
              </p>
              <p className="font-display text-2xl md:text-3xl leading-snug text-[var(--brown)]">
                « Des récits où l'Histoire, les civilisations, la mémoire et les choix humains
                ouvrent de nouvelles routes. »
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.5, duration: 0.8 }}
              className="mt-12 flex flex-wrap gap-4"
            >
              <Link to="/romans" data-testid="hero-cta-romans" className="btn-editorial filled">
                Découvrir les romans
              </Link>
              <Link to="/auteur" data-testid="hero-cta-auteur" className="btn-editorial">
                Découvrir l'auteur
              </Link>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.8, duration: 1 }}
            className="col-span-12 md:col-span-4 flex md:justify-end items-end mt-20 md:mt-0"
          >
            <div className="flex items-center gap-6">
              <div className="compass" />
              <div>
                <p className="eyebrow" style={{ color: "var(--copper)" }}>Cap</p>
                <p className="font-display text-2xl text-[var(--ink)]">Orient · 1540</p>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Scroll cue */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2 text-[var(--brown)]/60">
          <span className="text-[10px] font-mono uppercase tracking-widest">Descendre</span>
          <motion.span
            animate={{ y: [0, 8, 0] }}
            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
            className="h-8 w-[1px] bg-current"
          />
        </div>
      </section>

      {/* ============ FEATURED NOVEL — MALO ============ */}
      {featured && (
        <section className="theme-parchment px-6 lg:px-12 py-24 md:py-36 relative overflow-hidden" data-testid="featured-novel">
          <div className="max-w-[1440px] mx-auto grid grid-cols-12 gap-8">
            <div className="col-span-12 md:col-span-5">
              <div className="book-frame max-w-[420px]">
                {featured.cover_url ? (
                  <SafeImage src={featured.cover_url} alt={featured.title} />
                ) : featured.hero_image_url ? (
                  <SafeImage src={featured.hero_image_url} alt={featured.title} />
                ) : (
                  <div className="cover-placeholder">
                    <p className="eyebrow" style={{ color: "var(--gold-soft)" }}>Bruno Courtin</p>
                    <div>
                      <h3 className="font-display text-4xl leading-none">{featured.title}</h3>
                      {featured.subtitle && <p className="font-display italic text-xl mt-2">{featured.subtitle}</p>}
                    </div>
                    <div className="rule-copper" />
                  </div>
                )}
              </div>
            </div>

            <div className="col-span-12 md:col-span-7 md:pl-8 flex flex-col justify-center">
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7 }}
                className="eyebrow" style={{ color: "var(--copper)" }}
              >
                Roman à découvrir
              </motion.p>
              <h2 className="font-display font-light text-6xl md:text-7xl lg:text-8xl leading-[0.92] text-[var(--ink)] mt-4">
                {featured.title}
              </h2>
              {featured.subtitle && (
                <p className="font-display italic text-3xl md:text-4xl text-[var(--brown)] mt-2">
                  {featured.subtitle}
                </p>
              )}
              {featured.trilogy_tome > 0 && featured.trilogy?.length > 1 && (
                <div className="ornament mt-8 max-w-md">
                  <span className="font-mono text-[11px] tracking-widest uppercase">
                    Tome {featured.trilogy_tome} · Cycle de {featured.trilogy.length} tomes
                  </span>
                </div>
              )}
              <p className="mt-6 text-[15px] md:text-[16px] leading-[1.85] text-[var(--brown)]/90 max-w-xl">
                {featured.short_description}
              </p>
              <div className="mt-10 flex flex-wrap gap-4">
                <Link to={`/romans/${featured.slug}`} className="btn-editorial filled" data-testid="featured-cta-discover">
                  Découvrir le roman
                </Link>
                <Link to="/editeurs-agents" className="btn-editorial" data-testid="featured-cta-dossier">
                  Télécharger le dossier
                </Link>
              </div>

              <div className="mt-12 grid grid-cols-3 gap-6 max-w-xl">
                <div>
                  <p className="eyebrow" style={{ color: "var(--copper)" }}>Genre</p>
                  <p className="font-display text-xl mt-2 text-[var(--ink)]">{featured.genre || "—"}</p>
                </div>
                <div>
                  <p className="eyebrow" style={{ color: "var(--copper)" }}>Période</p>
                  <p className="font-display text-xl mt-2 text-[var(--ink)]">{featured.period || "—"}</p>
                </div>
                <div>
                  <p className="eyebrow" style={{ color: "var(--copper)" }}>Statut</p>
                  <p className="font-display text-xl mt-2 text-[var(--ink)]">{{ publie: "Publié", a_paraitre: "À paraître", en_preparation: "En préparation" }[featured.status] || "Statut à préciser"}</p>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ============ SLOW EDITORIAL MARQUEE ============ */}
      <div className="theme-ivory">
        <Marquee items={["1540", "Saint-Malo", "Océan Indien", "Madagascar", "Cochin", "Malacca", "Chine", "Japon", "La Vigilante"]} speed={70} />
      </div>

      {/* ============ NOVELS CATALOGUE ============ */}
      <section className="theme-ivory px-6 lg:px-12 py-24 md:py-36" data-testid="novels-section">
        <div className="max-w-[1440px] mx-auto">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-16 md:mb-20 gap-6">
            <div>
              <p className="eyebrow" style={{ color: "var(--copper)" }}>L'œuvre</p>
              <h2 className="font-display text-5xl md:text-6xl lg:text-7xl mt-3 text-[var(--ink)] leading-[0.95]">
                Les romans<br/>
                <span className="italic text-[var(--brown)]">en cours & à venir</span>
              </h2>
            </div>
            <Link to="/romans" className="btn-editorial" data-testid="see-all-novels">
              Voir tout le catalogue
            </Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-16">
            {novels.slice(0, 6).map((n, i) => (
              <BookCard key={n.id || n.slug} novel={n} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* ============ DÉMARCHE MANIFESTO ============ */}
      <section className="theme-ink px-6 lg:px-12 py-24 md:py-36" data-testid="manifesto-section">
        <div className="max-w-[1440px] mx-auto">
          <div className="grid grid-cols-12 gap-6 mb-16">
            <div className="col-span-12 md:col-span-4">
              <p className="eyebrow" style={{ color: "var(--gold-soft)" }}>Démarche</p>
            </div>
            <div className="col-span-12 md:col-span-8">
              <motion.h2
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.9 }}
                className="font-display text-4xl md:text-5xl lg:text-6xl leading-[1.05] text-[var(--ivory)]"
              >
                Une œuvre à la croisée de l'Histoire, des sciences humaines, de l'anticipation
                et des grandes questions contemporaines.
              </motion.h2>
            </div>
          </div>

          <div className="mt-12">
            <NumberedChapter number={1} title="Documentation historique et scientifique" index={0}>
              Un travail long, rigoureux, mené auprès des sources — pour donner au récit l'assise
              d'une vérité vécue plutôt qu'imaginée.
            </NumberedChapter>
            <NumberedChapter number={2} title="Force narrative" index={1}>
              Des intrigues denses, des personnages qui portent une trajectoire propre, des mondes
              qui se tiennent debout par la seule force du récit.
            </NumberedChapter>
            <NumberedChapter number={3} title="Profondeur humaine" index={2}>
              Ce que les grandes bascules — historiques, écologiques, civilisationnelles — font aux
              individus : c'est de cette échelle que parlent les livres.
            </NumberedChapter>
            <NumberedChapter number={4} title="Ouverture internationale" index={3}>
              Des récits qui traversent les océans, les langues, les civilisations. Les frontières
              sont matière romanesque, non horizon.
            </NumberedChapter>
          </div>

          <div className="mt-16 flex flex-wrap gap-4">
            <Link to="/auteur" className="btn-editorial filled" data-testid="cta-discover-author">
              Découvrir l'auteur
            </Link>
          </div>
        </div>
      </section>

      {/* ============ ACTUALITÉS ============ */}
      <section className="theme-parchment px-6 lg:px-12 py-24 md:py-36" data-testid="news-section">
        <div className="max-w-[1440px] mx-auto">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-16 gap-6">
            <div>
              <p className="eyebrow" style={{ color: "var(--copper)" }}>Journal de bord</p>
              <h2 className="font-display text-5xl md:text-6xl mt-3 text-[var(--ink)]">Actualités récentes</h2>
            </div>
            <Link to="/actualites" className="btn-editorial" data-testid="see-all-news">Toutes les actualités</Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-x-8 gap-y-12">
            {news.map((n, i) => (
              <motion.article
                key={n.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: i * 0.1 }}
                data-testid={`news-card-${n.id}`}
                className="group"
              >
                <Link to={`/actualites`}>
                  <div className="aspect-[4/3] overflow-hidden bg-[var(--ink)]">
                    {n.image_url && (
                      <SafeImage src={n.image_url} alt={n.title} className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105" />
                    )}
                  </div>
                  <p className="eyebrow mt-5" style={{ color: "var(--copper)" }}>{n.category}</p>
                  <h3 className="font-display text-2xl md:text-3xl mt-3 text-[var(--ink)] leading-tight">{n.title}</h3>
                  {n.summary && <p className="mt-3 text-[14px] leading-relaxed text-[var(--brown)]/80">{n.summary}</p>}
                </Link>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      {/* ============ PROFESSIONNELS ============ */}
      <section className="theme-ink px-6 lg:px-12 py-24 md:py-36 relative overflow-hidden" data-testid="pro-section">
        <div className="max-w-[1440px] mx-auto grid grid-cols-12 gap-8">
          <div className="col-span-12 md:col-span-5">
            <p className="eyebrow" style={{ color: "var(--gold-soft)" }}>Espace pro</p>
            <h2 className="font-display text-5xl md:text-6xl lg:text-7xl mt-4 leading-[0.95] text-[var(--ivory)]">
              Éditeurs, agents<br/><span className="italic">& producteurs</span>
            </h2>
          </div>
          <div className="col-span-12 md:col-span-7 md:pt-16">
            <p className="text-[17px] leading-[1.9] max-w-xl opacity-90">
              Retrouvez les dossiers de présentation, les synopsis, les notes d'intention, les
              informations relatives aux droits et les coordonnées professionnelles de l'auteur.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/editeurs-agents" className="btn-editorial filled" data-testid="pro-cta-space">
                Accéder à l'espace professionnel
              </Link>
              <Link to="/contact" className="btn-editorial" data-testid="pro-cta-contact">
                Contacter l'auteur
              </Link>
            </div>

            <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-4">
              {["Édition FR", "Droits étrangers", "Traduction", "Audio", "BD", "Cinéma", "Série", "Adaptation"].map((r) => (
                <div key={r} className="border border-[var(--gold)]/30 px-4 py-3 text-[11px] font-mono uppercase tracking-widest opacity-90">
                  {r}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
